import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { cva, VariantProps } from 'class-variance-authority'
import { Check, CircleCheckIcon } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

const checkboxVariants = cva(
  'peer h-4 w-4 shrink-0 rounded-sm border focus-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
  {
    variants: {
      variant: {
        noBorder: '',
        primary: 'border-primary',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
)

type CheckboxProps = React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & VariantProps<typeof checkboxVariants>

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(({ className, variant, ...props }, ref) => (
  <CheckboxPrimitive.Root ref={ref} className={cn(checkboxVariants({ variant, className }))} {...props}>
    <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
      <Check className="h-4 w-4" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

const CheckboxCard = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & { label: string; icon?: React.ReactNode }
>(({ className, label, icon, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'relative rounded-lg px-4 py-3 text-start text-muted-foreground ring-[1px] ring-border data-[state=checked]:text-primary data-[state=checked]:ring-2 data-[state=checked]:ring-primary',
      className,
    )}
    {...props}
  >
    {icon}
    <span className="text-md tracking-tight">{label}</span>
    <CheckboxPrimitive.Indicator className="absolute right-2 top-2">
      <CircleCheckIcon className="fill-primary text-primary-foreground" />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
CheckboxCard.displayName = 'CheckboxCard'

const CheckboxIndicator = CheckboxPrimitive.Indicator

export { Checkbox, CheckboxIndicator, CheckboxCard }
